import React, { useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Users } from "../api/models/user.models";
import { Company } from "../api/models/companies.models";
import { useRolesForSelect } from "../utils/roleUtils";
import {
  HttpRequestStatus,
  LoadingContainer,
} from "../components/loadingContainer";
import { Checkbox } from "primereact/checkbox";
import useAllOrganizationPermissions from "../hooks/useAllOrganizationPermissions";

const StyledDialog = styled(Dialog)`
  .p-dialog-content {
    padding: 0;
    display: flex;
    flex-direction: column;
  }
  .form-container {
    padding: 0 2rem;
    flex: 1;
    display: flex;
    flex-direction: column;

    & .p-button {
      max-width: 200px;
      margin-left: auto;
    }
  }

  .p-multiselect-token-label {
    font-size: 0.875rem;
  }
`;

const DialogActions = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 2rem;
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 1;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.1);
  padding: 1rem 2rem;
`;

const GridContainer = styled.div`
  display: grid;
  margin-top: 1rem;
  grid-template-columns: minmax(400px, 1fr) repeat(auto-fit, minmax(80px, 1fr));
  gap: 1rem;

  & label {
    text-align: center;
  }
`;

const CompanyNameLabel = styled.label`
  margin-bottom: auto;
  font-weight: 600;
  font-size: 1rem;
`;

const CompanyAndOrganizationContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  text-align: left !important;

  & label {
    text-align: left !important;
  }

  & .company-name {
    font-weight: 600;
    font-size: 1rem;
  }

  & .organization-name {
    font-size: 1rem;
    padding-left: 1rem;
  }
`;

interface AdminEditUserRolesDialogProps {
  show: boolean;
  onHide: () => void;
  user: Users;
  companies: Company[];
}

const AdminEditUserRolesDialog: React.FC<AdminEditUserRolesDialogProps> = ({
  show,
  onHide,
  user,
  companies,
}) => {
  const { t } = useTranslation();
  const [groupedPermissions, setGroupedPermissions] = React.useState<
    {
      companyId: number;
      organizationId: number | null;
      roleIds: number[];
    }[]
  >([]);

  const roleOptions = useRolesForSelect();

  const { fetchAllPermissions, permissionsData, permissionsIsLoading } =
    useAllOrganizationPermissions({
      userId: user.id,
      isAdmin: true,
    });

  useEffect(() => {
    if (show) {
      fetchAllPermissions();
    }
  }, [show]);

  useEffect(() => {
    if (permissionsData && permissionsData.length > 0) {
      const grouped: {
        companyId: number;
        organizationId: number | null;
        roleIds: number[];
      }[] = [];

      // Handle the nested structure from admin API
      // permissionsData is an array of objects with structure:
      // { companyId, organizationId, userId, permissions: [{id, name}] }
      permissionsData.forEach((companyPermission: any) => {
        const companyId = companyPermission.companyId;
        const organizationId = companyPermission.organizationId ?? null;

        if (!companyId) return;

        // Extract permission IDs from the nested permissions array
        const roleIds = companyPermission.permissions
          ? companyPermission.permissions.map((p: any) => p.id)
          : [];

        grouped.push({
          companyId,
          organizationId,
          roleIds,
        });
      });

      setGroupedPermissions(grouped);
    }
  }, [permissionsData]);

  const getCompanyName = (companyId: number): string => {
    return (
      companies.find((c) => c.id === companyId)?.companyName ||
      `Company ID: ${companyId}`
    );
  };

  const getOrganizationName = (
    companyId: number,
    organizationId: number | null
  ): string | null => {
    if (!organizationId) return null;

    const company = companies.find((c) => c.id === companyId);
    const organization = company?.organizations?.find(
      (org) => org.id === organizationId
    );
    return organization?.organizationName || null;
  };

  // Helper function to check if a permission exists at company level (main level)
  const isPermissionGrantedAtCompanyLevel = (
    companyId: number,
    roleId: number
  ): boolean => {
    const companyLevelPermissions = groupedPermissions.find(
      (cr) => cr.companyId === companyId && cr.organizationId === null
    );
    return companyLevelPermissions?.roleIds.includes(roleId) || false;
  };

  return (
    <StyledDialog
      header={t("label-select-user-access-and-roles")}
      visible={show}
      style={{ minWidth: "1200px", minHeight: "300px" }}
      onHide={onHide}
    >
      <LoadingContainer
        status={
          permissionsIsLoading
            ? HttpRequestStatus.LOADING
            : HttpRequestStatus.SUCCESS
        }
      >
        <div className="form-container">
          <GridContainer>
            <CompanyNameLabel></CompanyNameLabel>
            {roleOptions.map((role) => (
              <label
                key={role.value}
                style={{
                  fontWeight: 500,
                  fontSize: "1rem",
                }}
              >
                {role.label}
              </label>
            ))}
          </GridContainer>

          {groupedPermissions.map((companyAndRole, index) => {
            const isCompanyAdmin = companyAndRole.roleIds.includes(900);
            const isReportCreator = companyAndRole.roleIds.includes(400);
            const organizationName = getOrganizationName(
              companyAndRole.companyId,
              companyAndRole.organizationId
            );
            const isOrganizationLevel = companyAndRole.organizationId !== null;

            return (
              <GridContainer
                key={`${companyAndRole.companyId}-${
                  companyAndRole.organizationId || "main"
                }-${index}`}
              >
                <CompanyAndOrganizationContainer>
                  <CompanyNameLabel
                    htmlFor={`company-${companyAndRole.companyId}`}
                  >
                    {getCompanyName(companyAndRole.companyId)}
                  </CompanyNameLabel>
                  {organizationName && (
                    <span className="organization-name">
                      {`- `}
                      {organizationName}
                    </span>
                  )}
                </CompanyAndOrganizationContainer>

                {roleOptions.map((role) => {
                  const hasCompanyLevelPermission =
                    isOrganizationLevel &&
                    isPermissionGrantedAtCompanyLevel(
                      companyAndRole.companyId,
                      role.value as number
                    );

                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "nowrap",
                        gap: "0.6rem",
                        justifyContent: "center",
                      }}
                      key={role.value}
                    >
                      <Checkbox
                        id={`company-${companyAndRole.companyId}-org-${companyAndRole.organizationId}-role-${role.value}`}
                        checked={
                          companyAndRole.roleIds.includes(
                            role.value as number
                          ) ||
                          isCompanyAdmin ||
                          (isReportCreator && role.value === 300) ||
                          hasCompanyLevelPermission
                        }
                        onChange={() => {}}
                        disabled={true}
                      />
                    </div>
                  );
                })}
              </GridContainer>
            );
          })}
        </div>

        <DialogActions>
          <Button
            type="button"
            label={t("cmd-close")}
            className="p-button-text"
            onClick={onHide}
          />
        </DialogActions>
      </LoadingContainer>
    </StyledDialog>
  );
};

export default AdminEditUserRolesDialog;
